<template>
  <div class="navbar-container">
    <div v-if="!isMobile" class="logo-container" :class="{ 'no-shadow': activeOption === 0 }">
      <GallabLogo class="logo" size="150px" />
    </div>

    <div class="desktop-nav">
      <nav class="navbar" ref="navbar">
        <button v-for="(option, index) in navOptions" :key="index" class="navbar-button"
          :class="{ active: activeOption === index }" @click="setActive(index)"
          :style="{ zIndex: navOptions.length - index }">
          {{ option }}
        </button>
      </nav>
    </div>

    <button class="model-button" @click="toggleChangeModel">
      <span class="button-text">ZMIEŃ MODEL</span>
      <font-awesome-icon icon="right-left" size="md" />
    </button>

    <select class="mobile-select" v-model="activeOption" @change="handleSelectChange" v-if="isMobile">
      <option v-for="(option, index) in navOptions" :key="index" :value="index">
        {{ option }}
      </option>
    </select>

    <teleport to="body">
      <transition name="fade">
        <ModelChangeConfirmationComponent v-show="showChangeModel" @close="toggleChangeModel" />
      </transition>
    </teleport>
  </div>
</template>

<script>
import ModelChangeConfirmationComponent from '../model-select-page/ModelChangeConfirmationComponent.vue';
import GallabLogo from '@/components/common/GallabLogo.vue';

export default {
  name: 'NavbarComponent',
  components: {
    ModelChangeConfirmationComponent,
    GallabLogo
  },
  props: {
    navOptions: Array,
    activeIndex: Number
  },
  data() {
    return {
      activeOption: 0,
      isMobile: false,
      showChangeModel: false
    };
  },
  watch: {
    activeIndex(newVal) {
      this.activeOption = newVal;
      this.scrollToActive(newVal);
    }
  },
  mounted() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile);
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
    },
    setActive(index) {
      this.activeOption = index;
      this.$emit('scroll-to', index);

      this.scrollToActive(index);
    },
    handleSelectChange() {
      this.$emit('scroll-to', this.activeOption);
      this.scrollToActive(this.activeOption);
    },
    scrollToActive(index) {
      this.$nextTick(() => {
        const activeBtn = this.$refs.navbar?.children?.[index];
        if (activeBtn) {
          activeBtn.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }
      });
    },
    toggleChangeModel() {
      this.showChangeModel = !this.showChangeModel;
    }
  }
};
</script>

<style scoped lang="scss">
.navbar-container {
  display: flex;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: $color-white;
  box-shadow: 0 2px 10px $color-shadow;
  height: 70px;

  @media screen and (max-width: 768px) {
    height: 60px;
    gap: 10px;
  }
}

.model-button {
  display: flex;
  align-items: center;
  justify-content: right;
  padding-right: 20px;
  gap: 8px;
  width: auto;
  min-width: 200px;
  z-index: 8;
  margin-left: -25px;
  height: 70px;
  background-color: $color-white;
  color: $color-primary;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  clip-path: polygon(20px 50%,
      0 0,
      100% 0,
      100% 50%,
      100% 100%,
      0 100%,
      20px 50%);

  &:hover {
    background-color: $color-primary;
    transform: translateY(-1px);
    color: $color-white;
  }

  @media screen and (max-width: 768px) {
    padding: 8px 12px;
    font-size: 0.85rem;
    height: 100%
  }
}

.desktop-nav {
  flex-grow: 1;
  display: block;
  overflow: hidden;
  height: 100%;


  @media screen and (max-width: 768px) {
    display: none;
  }
}

.logo-container {
  width: 250px;
  height: 100%;
  padding-right: 15px;
  background-color: $color-navbar-button-light;
  z-index: 13;

  &.no-shadow {
    background-color: $color-primary;
  }

  @media screen and (max-width: 768px) {
    width: 150px;
    margin-right: 10px;
  }
}

.logo {
  background-color: transparent;
  margin-top: 20px;
  margin-left: 15px;
}

.navbar {
  display: flex;
  overflow-x: hidden;
  background-color: $color-white;
  height: 100%;
}

.navbar-button {
  position: relative;
  background-color: $color-navbar-button-light;
  color: #333;
  padding: 25px 30px;
  font-weight: 500;
  text-align: center;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
  flex-shrink: 0;

  clip-path: polygon(20px 50%,
      0 0,
      calc(100% - 20px) 0,
      100% 50%,
      calc(100% - 20px) 100%,
      0 100%,
      20px 50%);
  margin-right: -15px;


  &:hover {
    background-color: #c3ccae;
  }

  &.active {
    background-color: $color-primary;
    color: white;
  }

  &:first-child {
    clip-path: polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%);
  }

  &:not(:first-child)::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    width: 15px;
    height: 100%;
    background-color: inherit;
    clip-path: polygon(0 50%, 100% 0, 100% 100%);
  }
}

.mobile-select {
  flex-grow: 1;
  padding: 8px 12px;
  border: 2px solid $color-grey;
  border-radius: 20px;
  background-color: $color-white;
  font-size: 0.85rem;
  cursor: pointer;

  &:hover {
    border-color: $color-primary;
  }

  &:focus {
    outline: none;
    border-color: $color-primary;
  }

  @media screen and (min-width: 769px) {
    display: none;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>